/**
 * The fliers (02 §5): Pulpul (D_EFC0: 2 px across, 1 px up or down a tick) and Monsta (D_EEB2:
 * 2 and 2, 45 degrees, its sprite flipping as it bounces). Both ignore the players, bounce off
 * walls, ceilings and floors, and wrap through the open holes. Joystick bits: 1 left (else right),
 * 4 up, 8 down.
 */

import { hit, hitRow, probe, probeAt, type Probe } from './map';
import { animate, cycleFour } from './motion';
import { WALK_STEP, WRAP_FROM_Y, WRAP_TO_Y } from './tuning';
import type { Monster, World } from '../types';

/** L_EF8F: bounce vertically (flip the up/down bits). */
const bounceY = (m: Monster): void => {
  m.ai.joy ^= 0x0c;
};

/**
 * D_EF4C (up) / D_EFA0 (down): move `dy` px unless on the row grid with a solid row ahead - the row
 * above the body going up (checked one row up: pointer - 40, offsets 1..3), the row under the feet
 * going down. At the top (Y $15) going up it wraps to $F5 if the floor row is open there (a bottom
 * hole, $88C1); at the bottom (Y >= $F5) going down to $15 if the top row is open ($8501).
 */
function moveVertical(world: World, m: Monster, p: Probe, up: boolean, speed: number): void {
  if (p.subY !== 0) {
    m.y = (m.y + (up ? -speed : speed)) & 0xff;
    return;
  }
  if (up && m.y === WRAP_TO_Y) {
    // `sbc #$13` after an equal compare (carry set): the floor-row byte at column (X-19)/8 + 1.
    if (world.playfield.solid[24 * 32 + (((m.x - 0x13) & 0xff) >> 3) + 1] === 1) bounceY(m);
    else m.y = WRAP_FROM_Y;
    return;
  }
  if (!up && m.y >= WRAP_FROM_Y) {
    if (world.playfield.solid[(((m.x - 0x14) & 0xff) >> 3) + 1] === 1) bounceY(m);
    else m.y = WRAP_TO_Y;
    return;
  }
  const blocked = up ? hitRow(world, { ...p, row: p.row - 1 }, 0x01) : hitRow(world, p, 0x51);
  if (blocked) bounceY(m);
  else m.y = (m.y + (up ? -speed : speed)) & 0xff;
}

/**
 * D_EEEB / L_EF2A: 2 px across unless on the column grid with a wall beside the body (rows r, r+1,
 * and r+2 when off the row grid); a wall flips the left/right bits, and for Monsta the sprite too
 * (the `sty D_EF15` trick turning the RTS into `lda D_8520,x; eor #$04`).
 */
function moveAcross(world: World, m: Monster, p: Probe, left: boolean, flip: boolean): void {
  const base = left ? 0x00 : 0x03;
  const wall = p.subX === 0
    && (hit(world, p, base) || hit(world, p, base + 0x28) || (p.subY !== 0 && hit(world, p, base + 0x50)));
  if (!wall) {
    m.x = (m.x + (left ? -WALK_STEP : WALK_STEP)) & 0xff;
    return;
  }
  m.ai.joy ^= 3;
  if (flip) m.ai.frame ^= 4;
}

/** D_EFC0: Pulpul, 1 px vertically; frames cycle 0..3 and never flip. */
export function pulpulUpdate(world: World, m: Monster): void {
  const joy = m.ai.joy;
  moveVertical(world, m, probe(m), (joy & 4) !== 0, 1);
  moveAcross(world, m, probeAt(m.x, m.y), (joy & 1) !== 0, false);
  cycleFour(m);
}

/**
 * D_EEB2: Monsta, 2 px vertically. Across, a mismatched sprite (D_EEDF/D_EF1E `cmp #$04` on the
 * frame at the tick start) first turns instead of moving.
 */
export function monstaUpdate(world: World, m: Monster): void {
  const joy = m.ai.joy;
  const frame0 = m.ai.frame;
  moveVertical(world, m, probe(m), (joy & 4) !== 0, 2);
  const left = (joy & 1) !== 0;
  if (left && frame0 < 4) m.ai.frame = 4;
  else if (!left && frame0 >= 4) m.ai.frame = 0;
  else moveAcross(world, m, probeAt(m.x, m.y), left, true);
  animate(m);
}
